/**
 * 统一请求封装（带鉴权）
 *
 * 约定后端返回：{ code, data, msg }
 * - code === 200（或 0）视为成功，resolve 整个响应体
 * - 401 未登录 / token 过期：清理登录态并跳转登录页
 * - 其他错误：弹出提示并 reject
 */
import axios from 'axios'
import { ElMessage } from 'element-plus'
import { getToken, removeToken } from './auth'

const SUCCESS_CODES = [200, 0]

const service = axios.create({
  baseURL: '',
  timeout: 15000
})

// ===== 请求拦截：自动携带 token =====
service.interceptors.request.use(
  config => {
    const token = getToken()
    if (token) {
      // 后端使用 @RequestParam String token 接收，必须放在查询参数中
      config.params = {
        ...(config.params || {}),
        token
      }

      // 同时放 Header，兼容其他鉴权方式
      config.headers['Authorization'] = `Bearer ${token}`
      config.headers['token'] = token
    }
    return config
  },
  error => Promise.reject(error)
)

// 跳登录页（避免循环依赖 router，使用 hash 直接跳转）
function redirectToLogin() {
  removeToken()
  const current = window.location.hash.replace(/^#/, '')
  if (current.startsWith('/login')) return

  const redirect = current ? `?redirect=${encodeURIComponent(current)}` : ''
  window.location.hash = `#/login${redirect}`
}

// ===== 响应拦截：统一错误处理 =====
service.interceptors.response.use(
  response => {
    const res = response.data

    // 非标准结构（如文件流）直接返回
    if (res === null || typeof res !== 'object' || !('code' in res)) {
      return res
    }

    if (SUCCESS_CODES.includes(res.code)) {
      return res
    }

    // 业务层 401
    if (res.code === 401) {
      ElMessage.error('登录已过期，请重新登录')
      redirectToLogin()
      return Promise.reject(new Error(res.msg || '未登录'))
    }

    ElMessage.error(res.msg || '请求失败')
    return Promise.reject(new Error(res.msg || '请求失败'))
  },
  error => {
    const status = error?.response?.status
    const msg = error?.response?.data?.msg

    if (status === 401 || status === 403) {
      ElMessage.error(status === 401 ? '登录已过期，请重新登录' : '没有操作权限')
      redirectToLogin()
    } else if (status === 404) {
      ElMessage.error('接口不存在')
    } else if (status >= 500) {
      ElMessage.error(msg || '服务器异常')
    } else if (error.code === 'ECONNABORTED') {
      ElMessage.error('请求超时，请稍后重试')
    } else {
      ElMessage.error(msg || '网络异常')
    }

    return Promise.reject(error)
  }
)

export default service
